import { useState } from "react";
import { api, type NotifyLevel } from "@shared/api";
import { useSession } from "@entities/session";
import { Dialog } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { RadioGroup, RadioItem } from "@shared/ui/radio";
import { Switch } from "@shared/ui/switch";

const LEVELS: { level: NotifyLevel; title: string }[] = [
  { level: "all", title: "Все сообщения" },
  { level: "mentions", title: "Только @упоминания" },
  { level: "none", title: "Ничего" },
];

/**
 * «Параметры уведомлений» из меню сервера — как в Discord: уровень для всего
 * сервера. Выбор сохраняется сразу, «Готово» только закрывает окно.
 */
export function NotifyDialog({
  serverName,
  level,
  onPick,
  onClose,
}: {
  serverName: string;
  level: NotifyLevel;
  onPick: (level: NotifyLevel) => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      title="Параметры уведомлений"
      onClose={onClose}
      actions={
        <Button type="button" onClick={onClose}>
          Готово
        </Button>
      }
    >
      <div className="dialog-sub">{serverName}</div>
      <div className="caption">Параметры уведомлений сервера</div>
      <RadioGroup
        value={level}
        aria-label="Параметры уведомлений сервера"
        onValueChange={(next) => onPick(next as NotifyLevel)}
      >
        {LEVELS.map((item) => (
          <RadioItem key={item.level} value={item.level} title={item.title} />
        ))}
      </RadioGroup>
      <div className="small muted">
        Свой уровень у канала задаётся в его меню, пункт «Уведомления». Он важнее уровня сервера.
      </div>
    </Dialog>
  );
}

/**
 * «Настройки конфиденциальности» из меню сервера — как в Discord.
 *
 * В Discord личные сообщения разрешаются по серверу, у нас настройка одна на
 * все серверы — так и написано под переключателем, чтобы его не считали
 * настройкой только этого сервера.
 */
export function PrivacyDialog({ serverName, onClose }: { serverName: string; onClose: () => void }) {
  const { me, patchMe } = useSession();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const allowed = me?.allow_dms ?? true;

  async function toggle(next: boolean) {
    if (!me) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.users.setPrivacy(next);
      patchMe({ ...me, allow_dms: next });
    } catch {
      setProblem("Не сохранилось — попробуйте ещё раз");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Настройки конфиденциальности"
      onClose={onClose}
      actions={
        <Button type="button" onClick={onClose}>
          Готово
        </Button>
      }
    >
      <div className="dialog-sub">{serverName}</div>
      <label className="switch-row">
        <div className="grow">
          <div className="switch-title">Личные сообщения</div>
          <div className="switch-note">
            Разрешать личные сообщения от тех, кто не в друзьях. Пока это одна настройка на все
            серверы — она же в «Настройках → Конфиденциальность».
          </div>
        </div>
        <Switch checked={allowed} disabled={busy} onCheckedChange={(next) => void toggle(next)} />
      </label>
      {problem && <div className="small danger-text">{problem}</div>}
    </Dialog>
  );
}
